import { Dispatch, SetStateAction, useCallback, useRef } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

import { UniformSearchButton } from "./uniform-search-button";
import { UniformSearchCloseButton } from "./uniform-search-close-button";

export interface SearchBarProps {
    searchStringValue: string;
    searchInputFieldFocused: boolean;
    setSearchString: (searchString: string) => void;
    setSearchInputFieldFocused: Dispatch<SetStateAction<boolean>>;
    clearSearchField: () => void;
    runSearch: () => void;
}

export const SearchBar = ({
    searchStringValue,
    searchInputFieldFocused,
    setSearchString,
    setSearchInputFieldFocused,
    clearSearchField,
    runSearch
}: SearchBarProps) => {
    const intl: ReturnType<typeof useIntl> = useIntl();

    let createAnalyticsEvent:
        | ReturnType<typeof useAnalyticsEvents>["createAnalyticsEvent"]
        | null = null;
    try {
        const analyticsHook = useAnalyticsEvents();
        createAnalyticsEvent = analyticsHook.createAnalyticsEvent;
    } catch (e) {
        // Analytics provider not available
    }

    const searchRef = useRef<HTMLInputElement>(null);

    const onSearchInputChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setSearchString(event.target.value);
    };

    const handleSearchBarFocus = () => {
        setSearchInputFieldFocused(true);
        if (createAnalyticsEvent) {
            createAnalyticsEvent({
                origin: "web",
                platform: "web",
                product: "marketing",
                source: "none",
                userIdType: "atlassianAccount",
                eventComponent: "searchInput",
                event: "clicked",
                eventType: "ui",
                action: "clicked",
                actionSubject: "component",
                attributes: {
                    url: location.href,
                    page_path: location.pathname,
                    eventComponent: "searchInput"
                }
            }).fire();
        }
    };

    const handleOnBlur = () => {
        if (searchStringValue === "") {
            setSearchInputFieldFocused(false);
        } else {
            setSearchInputFieldFocused(true);
        }
    };

    const handleSearchButtonClick = useCallback(() => {
        if (searchRef.current) {
            searchRef.current.focus();
        }

        runSearch();
    }, [runSearch]);

    const handleClearSearchButtonClick = () => {
        clearSearchField();
        if (searchRef.current) {
            searchRef.current.focus();
        }
    };

    const searchClassName = searchInputFieldFocused ? "search-active" : "";

    return (
        <form
            css={searchBarContainerStyle}
            onSubmit={(evt) => {
                evt.preventDefault();
                handleSearchButtonClick();
            }}
        >
            <input
                className={searchClassName}
                type="text"
                aria-label={
                    intl?.formatMessage({
                        id: "SearchBar.searchPlaceholder",
                        defaultMessage: "Search"
                    }) || "Search"
                }
                placeholder={
                    intl?.formatMessage({
                        id: "SearchBar.searchPlaceholder",
                        defaultMessage: "Search"
                    }) || "Search"
                }
                onFocus={handleSearchBarFocus}
                onBlur={handleOnBlur}
                onChange={onSearchInputChange}
                value={searchStringValue}
                ref={searchRef}
            />
            <div className={`search-buttons-container ${searchClassName}`}>
                {searchInputFieldFocused && (
                    <>
                        <UniformSearchCloseButton
                            onClick={handleClearSearchButtonClick}
                        />
                        <div className="search-buttons-divider" />
                    </>
                )}
                <UniformSearchButton />
            </div>
        </form>
    );
};

const searchBarContainerStyle = css({
    "height": token("space.500"),
    "marginBlockEnd": 0,
    "width": "100%",
    "marginInlineEnd": token("space.200"),
    "background": wacToken("color.neutral.light200"),
    "borderRadius": token("space.500"),
    "display": "flex",
    "flexWrap": "nowrap",
    "justifyContent": "space-between",
    "padding": `0 ${token("space.200")}`,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "input": {
        border: "none",
        background: "transparent",
        outline: "none",
        width: "100%",
        flexBasis: "90%",
        color: wacToken("color.primary.black")
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "input.search-active": {
        flexBasis: "80%"
    },

    [media.below.sm]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "input.search-active::placeholder": {
            color: "transparent"
        }
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".search-buttons-container": {
        "flexBasis": "10%",
        "display": "flex",
        "alignItems": "center",
        "justifyContent": "flex-end",
        "flexWrap": "nowrap",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".search-buttons-divider": {
            margin: `0 ${token("space.100")}`,
            flexBasis: "1%",
            height: token("space.300"),
            borderInlineStart: `${token("border.width")} solid ${wacToken(
                "color.primary.black"
            )}`
        }
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".search-buttons-container.search-active": {
        flexBasis: "20%"
    }
});
